import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Settings as SettingsIcon,
  Plus,
  X,
  Sparkles,
  Lock,
  ArrowRight,
  ClipboardList,
  Copy,
  Download,
  Eraser,
  Loader2,
  Paintbrush,
  StickyNote,
  FileText,
  Printer,
  Ban,
  CalendarClock,
  AlertTriangle,
  Megaphone,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  plannerAccess,
  getPlannerSchedule,
  savePlannerSchedule,
  listPlannerEvents,
  createPlannerEvent,
  updatePlannerEvent,
  deletePlannerEvent,
  plannerDayLabels,
  pushPlannerEvent,
  generateWorkLookFors,
  listActivities,
  getLrpSummativeChips,
  listUnitsWithUnscheduledLessons,
  exportUrl,
  getActivity,
} from "../lib/api";
import ScheduleRemainingLessonsDialog from "./ScheduleRemainingLessonsDialog";
import MarkTaughtButton from "./MarkTaughtButton";
import { subjectColor, tint, SUBJECT_OPTIONS_PLUS_PREP } from "../lib/subjectPalette";
import DatePickerPopover from "./DatePickerPopover";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Legacy bundled subjects that pre-date the split taxonomy. If a saved
// schedule still holds one of these, the cleanup banner offers a one-tap
// remap to a specific strand.
const LEGACY_SUBJECT_STRANDS = {
  "The Arts": ["Dance", "Drama", "Music", "Visual Arts"],
  "Health and Physical Education": ["Health", "Physical Education"],
  "Health & Physical Education": ["Health", "Physical Education"],
};

// Whole-box highlight colours for non-teaching days, shared across the
// day / week / month views so a busy grid is easy to scan.
const DAY_HL = {
  holiday: "#f7d0d0",   // soft red
  pa_day: "#c9ebd3",    // soft green
  weekend: "#dde1e6",   // light grey
  other: "#fbe3c8",     // field trip / custom (amber)
};
const dayHighlight = (kind) => DAY_HL[kind] || DAY_HL.other;


const PERIOD_DEFAULTS = (n) => {
  const slots = [];
  let h = 9, m = 0;
  for (let i = 1; i <= n; i++) {
    const start = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    m += 40;
    while (m >= 60) { h += 1; m -= 60; }
    const end = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    slots.push({ label: `Period ${i}`, start, end, kind: "period" });
    if (i === 2) { slots.push({ label: "Recess", start: end, start_h: h, kind: "break", end: addMin(end, 15) }); h = parseTime(addMin(end, 15)).h; m = parseTime(addMin(end, 15)).m; }
    if (i === 4) { slots.push({ label: "Lunch", start: end, kind: "break", end: addMin(end, 60) }); h = parseTime(addMin(end, 60)).h; m = parseTime(addMin(end, 60)).m; }
  }
  return slots.map(({ start_h, ...s }) => s);
};

function parseTime(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return { h, m };
}
function addMin(hhmm, mins) {
  let { h, m } = parseTime(hhmm);
  m += mins;
  while (m >= 60) { h += 1; m -= 60; }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmtLongDate = (d) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const fmtShortDate = (d) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

// ============================================================
// Pro upsell
// ============================================================
const ProUpsell = () => (
  <div className="ae-card p-10 text-center max-w-2xl mx-auto" data-testid="planner-upsell">
    <Lock className="h-10 w-10 mx-auto text-[#4a6984]/70 mb-4" />
    <h3 className="font-extrabold text-2xl text-[#1c2d3d] mb-2">Planner is a Pro feature</h3>
    <p className="text-[#4a6984] mb-6 max-w-md mx-auto">
      Day Plan, Week at a Glance, and Monthly Overview for your entire year. Lessons
      land on the calendar the moment you generate them.
    </p>
    <Link to="/pricing">
      <Button className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold" data-testid="planner-upgrade-btn">
        Upgrade to Pro
      </Button>
    </Link>
  </div>
);

// ============================================================
// Setup wizard
// ============================================================
const SetupWizard = ({ initial, onSaved }) => {
  const [step, setStep] = useState(1);
  const [numPeriods, setNumPeriods] = useState(initial?.periods?.filter(p => p.kind === "period").length || 5);
  const [periods, setPeriods] = useState(initial?.periods?.length ? initial.periods : PERIOD_DEFAULTS(5));
  const [scheduleType, setScheduleType] = useState(initial?.schedule_type || "weekly");
  const [cycleDays, setCycleDays] = useState(initial?.cycle_days || 5);
  const [anchorDate, setAnchorDate] = useState(
    initial?.cycle_anchors?.[0]?.date || isoDate(new Date())
  );
  const [subjectMap, setSubjectMap] = useState(initial?.subject_map || {});
  const [saving, setSaving] = useState(false);
  // Phase 109 — bulk-paint mode for Step 3. When `paintSubject` is non-null
  // (including the special "__eraser__" sentinel), clicking any grid cell
  // applies that subject to that cell. Lets a teacher fill a varied
  // schedule (e.g. Science on Day 1 P3, Day 3 P4, Day 5 P2) without
  // touching three separate dropdowns.
  const [paintSubject, setPaintSubject] = useState(null);
  // Phase 83c.2 — Step 4 captures PA days + holidays so the rotation
  // cycle can pause on those dates from day one.
  const [daysOff, setDaysOff] = useState([]);  // [{date, kind, name?}]
  const [doDate, setDoDate] = useState("");
  const [doKind, setDoKind] = useState("pa_day");
  const [doName, setDoName] = useState("");

  // Regenerate period defaults whenever numPeriods changes (step 1 only)
  useEffect(() => {
    if (step === 1) setPeriods(PERIOD_DEFAULTS(numPeriods));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPeriods]);

  const dayLabels = scheduleType === "weekly"
    ? WEEKDAYS
    : Array.from({ length: cycleDays }, (_, i) => `Day ${i + 1}`);

  const teachingPeriods = periods.filter(p => p.kind === "period");

  const setCell = (day, period, value) => {
    setSubjectMap((m) => {
      const dayMap = { ...(m[day] || {}) };
      if (!value || value === "__none__") delete dayMap[period];
      else dayMap[period] = value;
      return { ...m, [day]: dayMap };
    });
  };

  // Bulk paint click handler — applies the current paint subject to the
  // clicked cell. Eraser clears the cell. No-op when paint mode is off.
  const paintCell = (day, period) => {
    if (!paintSubject) return;
    if (paintSubject === "__eraser__") {
      setCell(day, period, "");
    } else {
      setCell(day, period, paintSubject);
    }
  };

  // Mirror one column onto every other day — handy when 4 out of 5 days
  // share the same period→subject mapping and only one outlier differs.
  const copyDayToAll = (srcDay) => {
    const src = subjectMap[srcDay] || {};
    setSubjectMap((m) => {
      const next = { ...m };
      dayLabels.forEach((d) => {
        if (d !== srcDay) next[d] = { ...src };
      });
      return next;
    });
    toast.success(`Copied ${srcDay} to every day.`);
  };

  const clearDay = (day) => {
    setSubjectMap((m) => {
      const next = { ...m };
      next[day] = {};
      return next;
    });
  };

  const togglePeriodKind = (idx) => {
    setPeriods((arr) => arr.map((p, i) => i === idx ? { ...p, kind: p.kind === "period" ? "break" : "period" } : p));
  };

  const setPeriodTime = (idx, field, value) => {
    setPeriods((arr) => arr.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const setPeriodLabel = (idx, label) => {
    setPeriods((arr) => arr.map((p, i) => i === idx ? { ...p, label } : p));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        schedule_type: scheduleType,
        cycle_days: cycleDays,
        periods,
        subject_map: subjectMap,
        color_scheme: {},
        cycle_anchors: scheduleType === "rotation" ? [{ date: anchorDate, cycle_day: 1 }] : [],
        week_start: "monday",
        onboarded: true,
      };
      await savePlannerSchedule(payload);
      // Phase 83c.2 — persist any days-off captured in Step 4. Best-effort:
      // a failure to create one day-off shouldn't block the whole wizard.
      for (const d of daysOff) {
        try {
          await createPlannerEvent({
            date: d.date,
            kind: d.kind,
            activity_name: d.name || (d.kind === "pa_day" ? "PA Day" : "Statutory holiday"),
            subject: null,
            period_label: null,
          });
        } catch { /* ignore */ }
      }
      toast.success("Schedule saved.");
      onSaved(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const addDayOff = () => {
    if (!doDate) { toast.error("Pick a date first."); return; }
    if (daysOff.some((x) => x.date === doDate)) {
      toast.error("That date is already marked.");
      return;
    }
    setDaysOff((arr) => [...arr, { date: doDate, kind: doKind, name: doName }].sort((a, b) => a.date.localeCompare(b.date)));
    setDoDate("");
    setDoName("");
    setDoKind("pa_day");
  };

  const removeDayOff = (iso) => {
    setDaysOff((arr) => arr.filter((x) => x.date !== iso));
  };

  return (
    <div className="ae-card p-6 md:p-8" data-testid="planner-wizard">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#96ded1]" : "bg-[#4a6984]/15"}`}
          />
        ))}
      </div>
      <h3 className="font-extrabold text-2xl text-[#1c2d3d] mb-1">
        {step === 1 && "Your daily schedule"}
        {step === 2 && "Week or rotation cycle?"}
        {step === 3 && "Subjects by period"}
        {step === 4 && "PA days & holidays"}
      </h3>
      <p className="text-[#4a6984] mb-6">
        {step === 1 && "Tell us how your school day is structured. You can change this anytime."}
        {step === 2 && "Some Ontario schools run a Day 1–Day 5 (or Day 6) cycle instead of Mon–Fri."}
        {step === 3 && "Fill in which subject you teach in each slot. Use Prep when students are with another teacher."}
        {step === 4 && "Mark any days off you already know. The rotation cycle pauses on these dates — so a PA Day on Friday slides Day-N to Monday. You can add more anytime from Days off in the planner toolbar."}
      </p>

      {step === 1 && (
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-semibold text-[#4a6984] mb-1 block">
              How many teaching periods are in your day?
            </span>
            <Select value={String(numPeriods)} onValueChange={(v) => setNumPeriods(Number(v))}>
              <SelectTrigger className="w-32" data-testid="wizard-num-periods">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[4, 5, 6, 7, 8].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <div className="space-y-2">
            <span className="text-sm font-semibold text-[#4a6984] mb-1 block">
              Period times & breaks
            </span>
            {periods.map((p, i) => (
              <div
                key={i}
                // Phase 132 — On mobile, stack the row vertically so
                // each control gets a full-width thumb target. Was
                // previously `flex items-center gap-2` which crammed
                // label + start + "–" + end + toggle into one row,
                // making the time inputs ~80px wide and unselectable.
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 p-3 sm:p-0 border sm:border-0 border-[#4a6984]/15 rounded-md sm:rounded-none bg-[#f4f5f7]/30 sm:bg-transparent"
                data-testid={`wizard-period-row-${i}`}
              >
                <Input
                  className="w-full sm:w-32 h-11 sm:h-10"
                  value={p.label}
                  onChange={(e) => setPeriodLabel(i, e.target.value)}
                  aria-label={`Period ${i + 1} label`}
                />
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Input
                    type="time"
                    className="flex-1 sm:w-28 h-11 sm:h-10"
                    value={p.start}
                    onChange={(e) => setPeriodTime(i, "start", e.target.value)}
                    aria-label={`Period ${i + 1} start time`}
                  />
                  <span className="text-[#4a6984] shrink-0">–</span>
                  <Input
                    type="time"
                    className="flex-1 sm:w-28 h-11 sm:h-10"
                    value={p.end}
                    onChange={(e) => setPeriodTime(i, "end", e.target.value)}
                    aria-label={`Period ${i + 1} end time`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => togglePeriodKind(i)}
                  className={`text-xs font-semibold px-3 py-2.5 sm:py-1 rounded h-11 sm:h-auto w-full sm:w-auto ${p.kind === "break" ? "bg-[#4a6984]/10 text-[#4a6984]" : "bg-[#96ded1]/30 text-[#1c2d3d]"}`}
                  data-testid={`wizard-period-toggle-${i}`}
                >
                  {p.kind === "break" ? "Break" : "Teaching"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setScheduleType("weekly")}
              className={`text-left p-4 rounded-lg border-2 ${scheduleType === "weekly" ? "border-[#1c2d3d] bg-[#96ded1]/20" : "border-[#4a6984]/15 bg-white"}`}
              data-testid="wizard-type-weekly"
            >
              <div className="font-bold text-[#1c2d3d]">Monday–Friday</div>
              <div className="text-sm text-[#4a6984] mt-1">Standard 5-day week. Subjects repeat every Monday.</div>
            </button>
            <button
              type="button"
              onClick={() => setScheduleType("rotation")}
              className={`text-left p-4 rounded-lg border-2 ${scheduleType === "rotation" ? "border-[#1c2d3d] bg-[#96ded1]/20" : "border-[#4a6984]/15 bg-white"}`}
              data-testid="wizard-type-rotation"
            >
              <div className="font-bold text-[#1c2d3d]">Rotation cycle</div>
              <div className="text-sm text-[#4a6984] mt-1">Day 1 – Day N. "Day 1" doesn't always fall on a Monday.</div>
            </button>
          </div>

          {scheduleType === "rotation" && (
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <label className="text-sm font-semibold text-[#4a6984]">
                Days in your cycle:
                <Select value={String(cycleDays)} onValueChange={(v) => setCycleDays(Number(v))}>
                  <SelectTrigger className="w-24 inline-flex ml-2" data-testid="wizard-cycle-days">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[4, 5, 6, 7, 8].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="text-sm font-semibold text-[#4a6984]">
                Today is:
                <Select value={anchorDate} onValueChange={setAnchorDate}>
                  <SelectTrigger className="w-44 inline-flex ml-2" data-testid="wizard-anchor-date">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Offer a small picker — last 10 weekdays as anchor candidates */}
                    {Array.from({ length: 10 }).map((_, i) => {
                      const d = addDays(new Date(), -i);
                      if (d.getDay() === 0 || d.getDay() === 6) return null;
                      const iso = isoDate(d);
                      return <SelectItem key={iso} value={iso}>{fmtShortDate(d)} → Day 1</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </label>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          {/* Phase 109 — clarity hint: per-day independence is the whole
              point of this step. Teachers running a Day 1-5 rotation often
              have Science on different periods on different days, etc. */}
          <div className="rounded-md border border-[#96ded1]/45 bg-[#96ded1]/15 p-3 text-xs text-[#1c2d3d]">
            <p className="font-extrabold uppercase tracking-[0.15em] text-[#1c9e7e]">
              Each day is independent
            </p>
            <p className="mt-1 text-[#4a6984] leading-relaxed">
              Day 1 Period 3 can be Science while Day 2 Period 4 is also Science — just pick the right subject in each cell. Use the row above the grid to <strong>bulk-paint</strong> the same subject across multiple cells, or the column menu (⋯) on a day header to copy one day&apos;s mapping to all the others.
            </p>
          </div>

          {/* Bulk paint toolbar — pick a subject to use as the brush, then
              click any grid cell to apply it. Eraser clears. Off by default. */}
          <div className="rounded-md border border-[#4a6984]/15 bg-white p-3 space-y-2" data-testid="wizard-paint-toolbar">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70 inline-flex items-center gap-1.5">
                <Paintbrush className="h-3 w-3" /> Bulk paint
                {paintSubject ? (
                  <span className="ml-1 normal-case font-medium text-[#1c9e7e]">
                    · click any cell to paint
                  </span>
                ) : (
                  <span className="ml-1 normal-case font-medium text-[#4a6984]/60">
                    · off (pick a subject below to turn on)
                  </span>
                )}
              </p>
              {paintSubject ? (
                <button
                  type="button"
                  onClick={() => setPaintSubject(null)}
                  data-testid="wizard-paint-off"
                  className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#4a6984]/70 hover:text-[#4a6984]"
                >
                  Turn off
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SUBJECT_OPTIONS_PLUS_PREP.map((s) => {
                const active = paintSubject === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPaintSubject(active ? null : s)}
                    data-testid={`wizard-paint-${s.replace(/\s+/g, "-")}`}
                    aria-pressed={active}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                      active
                        ? "bg-[#96ded1] border-[#1c9e7e] text-[#1c2d3d] shadow-sm"
                        : "bg-white border-[#4a6984]/25 text-[#4a6984]/85 hover:border-[#4a6984]/45"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setPaintSubject(paintSubject === "__eraser__" ? null : "__eraser__")}
                data-testid="wizard-paint-eraser"
                aria-pressed={paintSubject === "__eraser__"}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors inline-flex items-center gap-1 ${
                  paintSubject === "__eraser__"
                    ? "bg-[#fde9d2] border-[#b45309] text-[#b45309] shadow-sm"
                    : "bg-white border-[#4a6984]/25 text-[#4a6984]/85 hover:border-[#4a6984]/45"
                }`}
              >
                <Eraser className="h-3 w-3" /> Eraser
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wizard-subject-grid">
              <thead>
                <tr>
                  <th className="text-left p-2 text-[#4a6984] font-semibold">Period</th>
                  {dayLabels.map((d) => (
                    <th key={d} className="text-left p-2 text-[#4a6984] font-semibold">
                      <div className="flex items-center justify-between gap-1">
                        <span>{d}</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              data-testid={`wizard-day-menu-${d.replace(/\s+/g, "-")}`}
                              className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-[#4a6984]/10 text-[#4a6984]"
                              title={`${d} options`}
                              aria-label={`${d} options`}
                            >
                              <SettingsIcon className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border-[#4a6984]/15">
                            <DropdownMenuItem
                              onClick={() => copyDayToAll(d)}
                              data-testid={`wizard-day-copy-${d.replace(/\s+/g, "-")}`}
                              className="text-[#4a6984] focus:bg-[#f4f5f7]"
                            >
                              <Copy className="h-3.5 w-3.5 mr-2" />
                              Copy {d} to all days
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => clearDay(d)}
                              data-testid={`wizard-day-clear-${d.replace(/\s+/g, "-")}`}
                              className="text-[#b45309] focus:bg-[#fde9d2]/40"
                            >
                              <Eraser className="h-3.5 w-3.5 mr-2" />
                              Clear {d}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teachingPeriods.map((p) => (
                  <tr key={p.label} className="border-t border-[#4a6984]/10">
                    <td className="p-2 font-semibold text-[#1c2d3d] whitespace-nowrap">
                      {p.label}
                      <div className="text-xs text-[#4a6984]/70 font-normal">{p.start}–{p.end}</div>
                    </td>
                    {dayLabels.map((d) => {
                      const current = subjectMap[d]?.[p.label] || "";
                      const painting = !!paintSubject;
                      return (
                        <td key={d} className="p-1">
                          {painting ? (
                            // Paint mode — entire cell is a click target.
                            // We render the current subject (or em-dash)
                            // and apply the brush on click.
                            <button
                              type="button"
                              onClick={() => paintCell(d, p.label)}
                              data-testid={`wizard-cell-${d}-${p.label}`}
                              className={`h-9 w-full px-2 text-xs rounded border text-left transition-colors ${
                                current
                                  ? "bg-[#f4f5f7] border-[#4a6984]/25 text-[#1c2d3d] hover:border-[#1c9e7e] hover:bg-[#96ded1]/20"
                                  : "bg-white border-dashed border-[#4a6984]/30 text-[#4a6984]/55 hover:border-[#1c9e7e] hover:bg-[#96ded1]/20"
                              }`}
                              title={
                                paintSubject === "__eraser__"
                                  ? "Click to clear"
                                  : `Click to paint ${paintSubject}`
                              }
                            >
                              {current || "—"}
                            </button>
                          ) : (
                            <Select
                              value={current}
                              onValueChange={(v) => setCell(d, p.label, v)}
                            >
                              <SelectTrigger className="h-9 text-xs" data-testid={`wizard-cell-${d}-${p.label}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— None —</SelectItem>
                                {SUBJECT_OPTIONS_PLUS_PREP.map((s) => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="wizard-days-off">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
            <DatePickerPopover
              value={doDate}
              onChange={setDoDate}
              testid="wizard-do-date"
              placeholder="Pick a date"
            />
            <Select value={doKind} onValueChange={setDoKind}>
              <SelectTrigger data-testid="wizard-do-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pa_day">PA Day</SelectItem>
                <SelectItem value="holiday">Holiday</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Optional label (e.g. Family Day)"
              value={doName}
              onChange={(e) => setDoName(e.target.value)}
              data-testid="wizard-do-name"
              className="flex-1"
            />
            <Button
              type="button"
              onClick={addDayOff}
              className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
              data-testid="wizard-do-add"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>

          <div className="max-h-72 overflow-y-auto space-y-1 border-t border-[#4a6984]/10 pt-3">
            {daysOff.length === 0 ? (
              <p className="text-xs text-[#4a6984]/60 italic">
                No days off marked yet. You can skip this step and add them later from the Days off button in the planner toolbar.
              </p>
            ) : (
              daysOff.map((d) => (
                <div
                  key={d.date}
                  className="flex items-center justify-between text-xs px-2 py-1.5 bg-[#f4f5f7] rounded"
                  data-testid={`wizard-do-row-${d.date}`}
                >
                  <div>
                    <span className="font-bold text-[#1c2d3d]">{d.date}</span>
                    <span className="text-[#4a6984] ml-2">
                      · {d.kind === "pa_day" ? "PA Day" : "Holiday"}
                      {d.name ? ` · ${d.name}` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDayOff(d.date)}
                    aria-label="Remove"
                    className="text-[#c45a6b] hover:bg-white rounded p-0.5"
                    data-testid={`wizard-do-remove-${d.date}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-8 pt-4 border-t border-[#4a6984]/10">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1 || saving}
          data-testid="wizard-back"
        >
          Back
        </Button>
        {step < 4 ? (
          <Button
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
            onClick={() => setStep((s) => s + 1)}
            data-testid="wizard-next"
          >
            Next
          </Button>
        ) : (
          <Button
            className="bg-[#96ded1] hover:bg-[#96ded1]/90 text-[#1c2d3d] font-semibold"
            onClick={handleSave}
            disabled={saving}
            data-testid="wizard-save"
          >
            {saving ? "Saving…" : "Save schedule"}
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Schedule lesson dialog
// ============================================================
const ScheduleLessonDialog = ({ open, onOpenChange, date, periodLabel, subject, onScheduled }) => {
  const [mode, setMode] = useState("lesson"); // "lesson" | "work"
  const [activities, setActivities] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [workPrompt, setWorkPrompt] = useState("");
  const [workBusy, setWorkBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("lesson");
    setWorkPrompt("");
    setLoading(true);
    listActivities()
      .then((list) => {
        setActivities(list);
        const subjLower = (subject || "").toLowerCase().replace(/[^a-z]/g, "");
        const matches = subjLower
          ? list.filter((a) => {
              const aSubj = (a.inputs?.subject || "").toLowerCase().replace(/[^a-z]/g, "");
              return aSubj.includes(subjLower) || subjLower.includes(aSubj);
            })
          : list;
        setFiltered(matches.length ? matches : list);
      })
      .finally(() => setLoading(false));
  }, [open, subject]);

  const handlePick = async (activity) => {
    try {
      const ev = await createPlannerEvent({
        date,
        period_label: periodLabel,
        subject,
        activity_id: activity.id,
        activity_name: activity.activity_name,
        walt: activity.tagline || "",
        kind: "lesson",
      });
      toast.success(`Scheduled "${activity.activity_name}".`);
      onScheduled(ev);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule.");
    }
  };

  const handleSaveWorkPeriod = async () => {
    if (!workPrompt.trim()) return;
    setWorkBusy(true);
    try {
      // Create the event first so look-fors can attach to its id.
      const ev = await createPlannerEvent({
        date,
        period_label: periodLabel,
        subject,
        kind: "work",
        work_prompt: workPrompt.trim(),
        activity_name: "Work period",
      });
      const { look_fors } = await generateWorkLookFors({
        prompt: workPrompt.trim(),
        subject: subject || null,
        event_id: ev.id,
      });
      onScheduled({ ...ev, look_fors });
      toast.success("Look-fors ready.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate look-fors.");
    } finally {
      setWorkBusy(false);
    }
  };

  const visible = filtered.filter((a) =>
    (a.activity_name || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-xl max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="planner-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Plan this period</DialogTitle>
          <DialogDescription>
            {date} · {periodLabel} {subject ? `· ${subject}` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Mode pills */}
        <div className="inline-flex rounded-lg bg-[#f4f5f7] p-1 self-start">
          <button
            type="button"
            onClick={() => setMode("lesson")}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${mode === "lesson" ? "bg-white shadow-sm text-[#1c2d3d]" : "text-[#4a6984]"}`}
            data-testid="planner-mode-lesson"
          >
            <FileText className="h-3 w-3 inline mr-1" /> Schedule a lesson
          </button>
          <button
            type="button"
            onClick={() => setMode("work")}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${mode === "work" ? "bg-white shadow-sm text-[#1c2d3d]" : "text-[#4a6984]"}`}
            data-testid="planner-mode-work"
          >
            <ClipboardList className="h-3 w-3 inline mr-1" /> Work period
          </button>
        </div>

        {mode === "lesson" && (
          <>
            <Input
              placeholder="Search your library…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="planner-schedule-search"
            />
            <div className="max-h-72 overflow-y-auto space-y-1 mt-2">
              {loading && <p className="text-sm text-[#4a6984]/70 px-1">Loading library…</p>}
              {!loading && visible.length === 0 && (
                <p className="text-sm text-[#4a6984]/70 px-1">No lessons match.</p>
              )}
              {visible.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => handlePick(a)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-[#96ded1]/20 border border-[#4a6984]/10"
                  data-testid={`planner-pick-${a.id}`}
                >
                  <div className="font-semibold text-[#1c2d3d] text-sm">{a.activity_name}</div>
                  <div className="text-xs text-[#4a6984]/70">
                    {(a.inputs?.subject || "—")} · {(a.inputs?.grade || "—")}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {mode === "work" && (
          <div className="space-y-2">
            <p className="text-xs text-[#4a6984]">
              What's happening this period? Atomic Edu will write 4-5 look-fors you can use while you circulate.
            </p>
            <Textarea
              placeholder="e.g. Work period for the Figurative Language unit — students finishing their poem analysis"
              rows={3}
              value={workPrompt}
              onChange={(e) => setWorkPrompt(e.target.value)}
              data-testid="planner-work-prompt"
            />
            <div className="flex justify-end">
              <Button
                className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
                onClick={handleSaveWorkPeriod}
                disabled={workBusy || !workPrompt.trim()}
                data-testid="planner-work-generate"
              >
                {workBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                Generate look-fors
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Link
            to={`/?subject=${encodeURIComponent(subject || "")}&grade=${encodeURIComponent("")}`}
            className="text-sm text-[#1c2d3d] underline font-semibold"
            data-testid="planner-generate-new"
          >
            Generate a new {subject ? subject : "lesson"} →
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Prep-slot notes dialog
// ============================================================
const PrepNoteDialog = ({ open, onOpenChange, target, onSaved, onRemove }) => {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setNotes(target?.event?.notes || "");
  }, [open, target]);

  if (!target) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      let event;
      if (target.event) {
        event = await updatePlannerEvent(target.event.id, { notes });
      } else {
        event = await createPlannerEvent({
          date: target.date,
          period_label: target.period_label,
          kind: "prep_note",
          notes,
          activity_name: "Prep period",
        });
      }
      onSaved(event);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="planner-prep-dialog">
        <DialogHeader>
          <DialogTitle>Prep period notes</DialogTitle>
          <DialogDescription>
            {target.date} · {target.period_label}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={"e.g.\n• Mark math exit tickets\n• Call Aiden's parents re: missing work\n• Photocopy reading handouts for Thursday"}
          data-testid="planner-prep-notes"
        />
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {target.event && (
            <Button
              variant="outline"
              onClick={() => { onRemove(target.event); onOpenChange(false); }}
              className="text-[#c45a6b] border-[#c45a6b]/30"
              data-testid="planner-prep-clear"
            >
              Clear
            </Button>
          )}
          <Button
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
            onClick={handleSave}
            disabled={saving}
            data-testid="planner-prep-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Lesson resource dialog — click a scheduled lesson to expand
// ============================================================
const LessonResourceDialog = ({ open, onOpenChange, event, onUpdate, onRemove, onPushed }) => {
  const [notes, setNotes] = useState(event?.notes || "");
  const [pushing, setPushing] = useState(false);
  const [confirmPush, setConfirmPush] = useState(false);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    setNotes(event?.notes || "");
    setConfirmPush(false);
  }, [event]);

  // Fetch the linked activity so we can surface its live `taught` state
  // and reuse the shared MarkTaughtButton (with cascade support).
  const refetchActivity = useCallback(async () => {
    if (!event?.activity_id) {
      setActivity(null);
      return;
    }
    try {
      const a = await getActivity(event.activity_id);
      setActivity(a);
    } catch {
      setActivity(null);
    }
  }, [event?.activity_id]);

  useEffect(() => {
    if (open) refetchActivity();
  }, [open, refetchActivity]);

  // Debounced auto-save for notes.
  useEffect(() => {
    if (!event) return;
    if ((event.notes || "") === notes) return;
    const t = setTimeout(async () => {
      try {
        const updated = await updatePlannerEvent(event.id, { notes });
        onUpdate(updated);
      } catch {
        toast.error("Could not save note.");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [notes, event, onUpdate]);

  if (!event) return null;
  const id = event.activity_id;
  const subj = event.subject || "";
  const color = subjectColor(subj);

  const handlePush = async (cascade) => {
    setPushing(true);
    try {
      const res = await pushPlannerEvent(event.id, cascade);
      toast.success(
        cascade
          ? `Pushed ${res.pushed} lesson${res.pushed === 1 ? "" : "s"} forward.`
          : "Lesson pushed to the next slot."
      );
      onPushed(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Push failed.");
    } finally {
      setPushing(false);
      setConfirmPush(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="planner-resource-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="h-10 w-1 rounded" style={{ backgroundColor: color }} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-[#4a6984]/70 font-bold">
                {event.date} · {event.period_label} {subj ? `· ${subj}` : ""}
              </div>
              <DialogTitle data-testid="planner-resource-title">{event.activity_name}</DialogTitle>
              {event.walt && <DialogDescription className="mt-1">{event.walt}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>

        {/* Mark as taught — only for real lesson activities. */}
        {activity?.id && (
          <div
            className="flex items-center justify-between gap-3 rounded-md border border-[#4a6984]/15 bg-[#f4f5f7]/40 px-3 py-2"
            data-testid="planner-resource-taught"
          >
            <span className="text-xs font-semibold text-[#4a6984]">
              {activity.taught
                ? "This lesson is marked taught."
                : "Taught it? Mark it so its expectations feed Live Reports."}
            </span>
            <MarkTaughtButton
              activity={activity}
              onChange={refetchActivity}
              size="sm"
            />
          </div>
        )}

        {/* Open the full lesson page */}
        {id && (
          <Link
            to={`/lesson/${encodeURIComponent(id)}`}
            data-testid="planner-resource-open-lesson"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto self-start rounded-md bg-[#1c2d3d] px-4 py-2 text-sm font-bold text-white hover:bg-[#1c2d3d]/90 transition-colors"
          >
            <FileText className="h-4 w-4" /> Open lesson page
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}

        {/* Downloads */}
        {id && (
          <div className="space-y-2">
            <h5 className="text-xs font-bold uppercase tracking-wider text-[#4a6984]">
              Download resources
            </h5>
            <div className="flex flex-wrap gap-2">
              {[
                ["pdf", "Lesson Information Sheet", FileText],
                ["script", "Teacher Plan", FileText],
                ["slides", "Student Slide Deck", FileText],
                ["worksheet", "Worksheet", FileText],
                ["docx", "Word", FileText],
              ].map(([fmt, label, Icon]) => (
                <a
                  key={fmt}
                  href={exportUrl(id, fmt)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#4a6984]/15 bg-white text-xs font-semibold text-[#1c2d3d] hover:bg-[#96ded1]/10"
                  data-testid={`planner-download-${fmt}`}
                >
                  <Icon className="h-3.5 w-3.5" /><Download className="h-3 w-3" /> {label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="space-y-1.5 mt-1">
          <h5 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1">
            <StickyNote className="h-3.5 w-3.5" /> Notes for yourself
          </h5>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Redo the warm-up — they didn't get it yesterday."
            rows={3}
            className="text-sm"
            data-testid="planner-resource-notes"
          />
          <p className="text-[10px] text-[#4a6984]/60">Auto-saves as you type. Private to you.</p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onRemove(event)}
            data-testid="planner-resource-remove"
            className="text-[#c45a6b] border-[#c45a6b]/30 hover:bg-[#c45a6b]/5"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Remove from planner
          </Button>
          {!confirmPush ? (
            <Button
              className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
              onClick={() => setConfirmPush(true)}
              data-testid="planner-resource-push"
              disabled={pushing}
            >
              <ArrowRight className="h-3.5 w-3.5 mr-1" /> Push forward
            </Button>
          ) : (
            <div className="flex flex-col gap-1.5 w-full sm:w-auto" data-testid="planner-push-confirm">
              <p className="text-xs text-[#4a6984]">
                Pushing this lesson will shift all upcoming {subj} lessons by one slot. Push all?
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-[#96ded1] hover:bg-[#96ded1]/90 text-[#1c2d3d] font-semibold"
                  onClick={() => handlePush(true)}
                  disabled={pushing}
                  data-testid="planner-push-all"
                >
                  {pushing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Push all
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePush(false)}
                  disabled={pushing}
                  data-testid="planner-push-one"
                >
                  Push this one only
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmPush(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Cell content (lesson chip / work period / prep notes / +)
// ============================================================
// One-line auto-saving "write-in" for a period (Library, Assembly, …).
const PeriodWriteIn = ({ metaEvent, date, periodLabel, onSaved, compact }) => {
  const [text, setText] = useState(metaEvent?.note || "");
  const [saved, setSaved] = useState(metaEvent?.note || "");
  const timer = useRef(null);
  useEffect(() => {
    setText(metaEvent?.note || "");
    setSaved(metaEvent?.note || "");
  }, [metaEvent?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (text === saved) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await onSaved(date, periodLabel, text, metaEvent);
        setSaved(text);
      } catch {
        /* toast handled upstream */
      }
    }, 700);
    return () => timer.current && clearTimeout(timer.current);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      placeholder="Library, assembly…"
      data-testid={`planner-writein-${date}-${periodLabel}`}
      className={`w-full rounded border border-[#4a6984]/20 bg-white text-[#1c2d3d] placeholder:text-[#4a6984]/45 focus:outline-none focus:border-[#1c9e7e] ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs"}`}
    />
  );
};

const SubjectCell = ({ subject, lessonEvent, metaEvent, date, periodLabel, onSchedule, onOpenLesson, onSaveWriteIn, isBreak, compact = false }) => {
  if (isBreak) {
    return <div className="text-xs text-[#4a6984]/40 italic px-2 py-1">{subject || "Break"}</div>;
  }
  const color = subjectColor(subject);
  const wsCount =
    (metaEvent?.links?.length || 0) +
    (metaEvent?.attachments?.length || 0) +
    ((metaEvent?.notes || "").trim() ? 1 : 0);
  const periodPath = `/planner/period/${date}/${encodeURIComponent(periodLabel)}`;

  return (
    <div className="flex flex-col gap-1" data-testid={`planner-period-cell-${date}-${periodLabel}`}>
      {/* Area 1 — the lesson slot (schedule a lesson / work period) */}
      {lessonEvent && lessonEvent.kind === "lesson" ? (
        <div
          className="rounded p-2 text-xs relative group cursor-grab active:cursor-grabbing"
          style={{ backgroundColor: tint(color, 0.25), borderLeft: `3px solid ${color}` }}
          onClick={onOpenLesson}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData("text/atomicedu-event", lessonEvent.id);
            e.dataTransfer.setData("text/atomicedu-subject", subject || "");
            e.dataTransfer.effectAllowed = "move";
          }}
          data-testid={`planner-event-${lessonEvent.id}`}
        >
          <div className="font-semibold text-[#1c2d3d] line-clamp-2 leading-tight">{lessonEvent.activity_name}</div>
          {lessonEvent.walt && <div className="text-[10px] text-[#4a6984] mt-0.5 line-clamp-2">{lessonEvent.walt}</div>}
          {lessonEvent.notes && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-[#4a6984]/70">
              <StickyNote className="h-2.5 w-2.5" /><span className="truncate">{lessonEvent.notes}</span>
            </div>
          )}
        </div>
      ) : lessonEvent && lessonEvent.kind === "work" ? (
        <div
          className="rounded p-2 text-xs cursor-pointer"
          style={{ backgroundColor: tint(color, 0.18), borderLeft: `3px solid ${color}` }}
          onClick={onOpenLesson}
          data-testid={`planner-work-${lessonEvent.id}`}
        >
          <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-[#4a6984]">
            <ClipboardList className="h-3 w-3" /> Work period
          </div>
          <div className="font-semibold text-[#1c2d3d] line-clamp-2 leading-tight mt-0.5">
            {lessonEvent.work_prompt || "Work period"}
          </div>
          {lessonEvent.look_fors?.length > 0 && (
            <div className="text-[10px] text-[#4a6984] mt-0.5">{lessonEvent.look_fors.length} look-fors</div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={onSchedule}
          className={`w-full flex items-center justify-center text-[#4a6984]/50 hover:text-[#4a6984] hover:bg-[#96ded1]/10 rounded text-xs ${compact ? "min-h-[2rem]" : "min-h-[2.5rem]"}`}
          style={{ borderLeft: `3px solid ${tint(color, 0.4)}` }}
          data-testid="planner-empty-slot"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          <span>{subject && subject !== "Prep" ? subject : "Add lesson"}</span>
        </button>
      )}

      {/* Area 2 — free-text write-in */}
      <PeriodWriteIn
        metaEvent={metaEvent}
        date={date}
        periodLabel={periodLabel}
        onSaved={onSaveWriteIn}
        compact={compact}
      />

      {/* Area 3 — clickable workspace page (notes / links / attachments) */}
      <Link
        to={periodPath}
        onClick={(e) => e.stopPropagation()}
        data-testid={`planner-period-workspace-${date}-${periodLabel}`}
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#4a6984] hover:text-[#1c9e7e]"
        title="Notes, links & attachments for this period"
      >
        <StickyNote className="h-3 w-3" />
        {compact ? null : <span>Notes &amp; files</span>}
        {wsCount > 0 && (
          <span className="ml-0.5 px-1 rounded-full bg-[#96ded1]/40 text-[#1c2d3d] text-[9px] font-bold">{wsCount}</span>
        )}
      </Link>
    </div>
  );
};

// ============================================================
// Day plan view
// ============================================================
const DayPlanView = ({ schedule, events, dayLabel, currentDate, onSchedule, onOpenLesson, onSaveWriteIn, onDropEvent, dayBlock }) => {
  if (!dayLabel) {
    return (
      <div className="ae-card overflow-hidden" data-testid="planner-day-view">
        <div className="px-5 py-3 bg-[#f4f5f7] border-b border-[#4a6984]/30">
          <h4 className="font-bold text-[#1c2d3d]">{fmtLongDate(currentDate)}</h4>
        </div>
        <div className="p-8 text-center" style={{ backgroundColor: DAY_HL.weekend }}>
          <p className="text-sm font-bold text-[#1c2d3d] uppercase tracking-wider">Weekend</p>
          <p className="text-[#4a6984] mt-1">No school day.</p>
        </div>
      </div>
    );
  }
  if (dayBlock) {
    return (
      <div className="ae-card overflow-hidden" data-testid="planner-day-view">
        <div className="px-5 py-3 bg-[#f4f5f7] border-b border-[#4a6984]/30">
          <h4 className="font-bold text-[#1c2d3d]">{fmtLongDate(currentDate)}</h4>
          <p className="text-xs text-[#4a6984]">{dayLabel}</p>
        </div>
        <div className="p-8 text-center" style={{ backgroundColor: dayHighlight(dayBlock.kind) }}>
          <p className="text-sm font-bold text-[#1c2d3d] uppercase tracking-wider">
            {dayBlock.kind === "holiday" ? "Holiday" : dayBlock.kind === "pa_day" ? "PA Day" : dayBlock.kind === "field_trip" ? "Field Trip" : "Day blocked"}
          </p>
          <p className="text-[#4a6984] mt-1">{dayBlock.activity_name || ""}</p>
        </div>
      </div>
    );
  }
  const dayPeriods = schedule.subject_map?.[dayLabel] || {};
  return (
    <div className="ae-card overflow-hidden" data-testid="planner-day-view">
      <div className="px-5 py-3 bg-[#f4f5f7] border-b border-[#4a6984]/10">
        <h4 className="font-bold text-[#1c2d3d]">{fmtLongDate(currentDate)}</h4>
        <p className="text-xs text-[#4a6984]">{dayLabel}</p>
      </div>
      <div className="divide-y divide-[#4a6984]/30">
        {schedule.periods.map((p) => {
          const subject = dayPeriods[p.label] || "";
          const isBreak = p.kind === "break";
          const iso = isoDate(currentDate);
          const lessonEvent = events.find(
            (e) => e.date === iso && e.period_label === p.label && (e.kind === "lesson" || e.kind === "work")
          );
          const metaEvent = events.find(
            (e) => e.date === iso && e.period_label === p.label && e.kind === "period_note"
          );
          const cellTarget = { date: iso, period_label: p.label, subject };
          return (
            <div
              key={p.label}
              className="grid grid-cols-[6rem_1fr] items-stretch"
              onDragOver={(e) => { if (!isBreak) e.preventDefault(); }}
              onDrop={(e) => {
                if (isBreak) return;
                const id = e.dataTransfer.getData("text/atomicedu-event");
                const draggedSubject = e.dataTransfer.getData("text/atomicedu-subject");
                if (id) onDropEvent({ eventId: id, target: cellTarget, draggedSubject });
              }}
            >
              <div className="px-4 py-2 bg-white text-xs">
                <div className="font-bold text-[#1c2d3d]">{p.label}</div>
                <div className="text-[#4a6984]/70">{p.start}–{p.end}</div>
              </div>
              <div className="p-2">
                <SubjectCell
                  subject={subject}
                  lessonEvent={lessonEvent}
                  metaEvent={metaEvent}
                  date={iso}
                  periodLabel={p.label}
                  isBreak={isBreak}
                  onSchedule={() => onSchedule(cellTarget)}
                  onOpenLesson={() => onOpenLesson(lessonEvent)}
                  onSaveWriteIn={onSaveWriteIn}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// Week view
// ============================================================
const WeekView = ({ schedule, events, dayLabels, dates, onSchedule, onOpenLesson, onSaveWriteIn, onDropEvent, blocksByDate, summativeChips = [] }) => {
  const teachingPeriods = schedule.periods;
  // Group chips by ISO date for O(1) lookup in the header.
  const chipsByDate = useMemo(() => {
    const m = {};
    (summativeChips || []).forEach((c) => {
      if (!m[c.date]) m[c.date] = [];
      m[c.date].push(c);
    });
    return m;
  }, [summativeChips]);
  return (
    <div className="ae-card overflow-hidden" data-testid="planner-week-view">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#f4f5f7]">
            <tr>
              <th className="text-left p-2 text-[#4a6984] font-semibold w-24">Period</th>
              {dates.map((d, i) => {
                const _hlBlk = blocksByDate[isoDate(d)];
                const _hl = _hlBlk ? dayHighlight(_hlBlk.kind) : (!dayLabels[i] ? DAY_HL.weekend : null);
                return (
                <th
                  key={d.toISOString()}
                  className="text-left p-2 text-[#4a6984] font-semibold border-l border-[#4a6984]/30"
                  style={_hl ? { backgroundColor: _hl } : undefined}
                >
                  <div className="font-bold text-[#1c2d3d]">{fmtShortDate(d)}</div>
                  <div className="text-[10px] text-[#4a6984]/70">{dayLabels[i] || "—"}</div>
                  {blocksByDate[isoDate(d)] && (
                    <div className="mt-1 text-[9px] uppercase font-bold tracking-wider text-[#c45a6b]" data-testid={`planner-week-block-${isoDate(d)}`}>
                      {blocksByDate[isoDate(d)].kind === "holiday" ? "Holiday" : blocksByDate[isoDate(d)].kind === "pa_day" ? "PA Day" : blocksByDate[isoDate(d)].kind === "field_trip" ? "Field Trip" : "Blocked"}
                    </div>
                  )}
                  {(chipsByDate[isoDate(d)] || []).map((c, idx) => (
                    <div
                      key={idx}
                      data-testid={`planner-summative-chip-${isoDate(d)}-${idx}`}
                      title={`Long Range Plan — ${c.subject?.replace("_", " ") || ""}`}
                      className="mt-1 inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded text-[9px] uppercase font-bold tracking-wider bg-[#96ded1]/45 text-[#1c2d3d] border border-[#96ded1]"
                    >
                      <span className="opacity-70">{c.type || "summative"}</span>
                      <span className="opacity-70">·</span>
                      <span className="truncate normal-case">{c.unit_name}</span>
                    </div>
                  ))}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {teachingPeriods.map((p) => (
              <tr key={p.label} className="border-t border-[#4a6984]/30">
                <td className="p-2 align-top whitespace-nowrap">
                  <div className="font-bold text-[#1c2d3d]">{p.label}</div>
                  <div className="text-[10px] text-[#4a6984]/70">{p.start}–{p.end}</div>
                </td>
                {dates.map((d, i) => {
                  const dLabel = dayLabels[i];
                  const isBreak = p.kind === "break";
                  const blocked = blocksByDate[isoDate(d)];
                  if (!dLabel || isBreak || blocked) {
                    // Highlight the whole non-teaching column: block kind,
                    // else weekend grey; a normal break stays neutral.
                    const cellHl = blocked ? dayHighlight(blocked.kind) : (!dLabel ? DAY_HL.weekend : null);
                    return <td
                      key={d.toISOString()}
                      className={`p-2 align-top border-l border-[#4a6984]/30 ${cellHl ? "" : "bg-[#4a6984]/5"}`}
                      style={cellHl ? { backgroundColor: cellHl } : undefined}
                    >
                      <div className="text-[10px] text-[#4a6984]/40 italic">{isBreak ? p.label : (blocked ? "—" : "—")}</div>
                    </td>;
                  }
                  const subject = schedule.subject_map?.[dLabel]?.[p.label] || "";
                  const iso = isoDate(d);
                  const lessonEvent = events.find(
                    (e) => e.date === iso && e.period_label === p.label && (e.kind === "lesson" || e.kind === "work")
                  );
                  const metaEvent = events.find(
                    (e) => e.date === iso && e.period_label === p.label && e.kind === "period_note"
                  );
                  const cellTarget = { date: iso, period_label: p.label, subject };
                  return (
                    <td
                      key={d.toISOString()}
                      className="p-1 align-top min-w-[8rem] border-l border-[#4a6984]/30"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const id = e.dataTransfer.getData("text/atomicedu-event");
                        const draggedSubject = e.dataTransfer.getData("text/atomicedu-subject");
                        if (id) onDropEvent({ eventId: id, target: cellTarget, draggedSubject });
                      }}
                    >
                      <SubjectCell
                        subject={subject}
                        lessonEvent={lessonEvent}
                        metaEvent={metaEvent}
                        date={iso}
                        periodLabel={p.label}
                        compact
                        onSchedule={() => onSchedule(cellTarget)}
                        onOpenLesson={() => onOpenLesson(lessonEvent)}
                        onSaveWriteIn={onSaveWriteIn}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// Month view
// ============================================================
const MonthView = ({ schedule, events, monthDate, dayLabelMap, onDayClick, blocksByDate, announcementsByDate, onEditAnnouncement }) => {
  // Build a 6×7 grid starting from the Sunday before the 1st. The month
  // calendar always runs Sunday → Saturday so the weekend days bookend
  // each week row.
  const weekStartIdx = 0;
  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const offset = (firstOfMonth.getDay() - weekStartIdx + 7) % 7;
  const gridStart = addDays(firstOfMonth, -offset);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  const headerLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="ae-card overflow-hidden" data-testid="planner-month-view">
      <div className="grid grid-cols-7 text-xs">
        {headerLabels.map((l) => (
          <div key={l} className="px-2 py-2 bg-[#f4f5f7] text-[#4a6984] font-semibold text-center border-b border-[#4a6984]/30">{l}</div>
        ))}
        {cells.map((d) => {
          const inMonth = d.getMonth() === monthDate.getMonth();
          const iso = isoDate(d);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const dayEvents = events.filter((e) => e.date === iso && e.kind === "lesson");
          const block = blocksByDate?.[iso];
          const isToday = iso === isoDate(new Date());
          // Colour the whole month cell: holiday soft red, PA day soft
          // green, weekend light grey, other blocks amber.
          let cellHl = null;
          if (block) cellHl = dayHighlight(block.kind);
          else if (isWeekend) cellHl = DAY_HL.weekend;
          const blockTone = block && block.kind === "pa_day" ? "text-[#2e7d4f]" : "text-[#c0392b]";
          const blockLabel = block
            ? (block.kind === "holiday" ? "Holiday" : block.kind === "pa_day" ? "PA Day" : block.kind === "field_trip" ? "Field Trip" : "Blocked")
            : "";
          // Show the teacher's optional label (activity_name) when it isn't
          // just the default kind name.
          const _defaultNames = ["PA Day", "Statutory holiday", "Holiday", "Field Trip", "Day off", "Blocked", ""];
          const blockCustom = block && block.activity_name && !_defaultNames.includes(block.activity_name.trim())
            ? block.activity_name.trim()
            : "";
          // A "school day" is an in-month weekday that isn't a holiday/PA
          // block — only these get the announcement / notes / day-plan trio.
          const isSchoolDay = inMonth && !isWeekend && !block;
          const announcement = announcementsByDate?.[iso];
          const annText = (announcement?.note || announcement?.activity_name || "").trim();
          return (
            <div
              key={iso}
              className={`min-h-[6.5rem] p-1.5 text-left border-r border-b border-[#4a6984]/30 flex flex-col ${inMonth ? "bg-white" : "bg-[#f4f5f7]/40"}`}
              style={cellHl ? { backgroundColor: cellHl } : undefined}
              data-testid={`planner-month-cell-${iso}`}
            >
              <div className={`text-xs font-bold ${inMonth ? "text-[#1c2d3d]" : "text-[#4a6984]/40"} ${isToday ? "ring-2 ring-[#96ded1] inline-block px-1 rounded self-start" : ""}`}>
                {d.getDate()}
              </div>
              {dayLabelMap?.[iso] && (
                <div className="text-[9px] text-[#4a6984]/60 leading-none">{dayLabelMap[iso]}</div>
              )}
              {block && (
                block.kind === "pa_day" ? (
                  <Link
                    to={`/planner/pa-day/${iso}`}
                    className="mt-0.5 leading-tight block rounded px-1 -mx-1 py-0.5 hover:bg-[#2e7d4f]/10 group"
                    data-testid={`planner-month-block-${iso}`}
                    title="Open this PA day"
                  >
                    <div className={`text-[9px] uppercase font-bold tracking-wider ${blockTone}`}>
                      {blockLabel}
                    </div>
                    {blockCustom && (
                      <div className={`text-[9px] font-semibold normal-case truncate ${blockTone}`}>
                        {blockCustom}
                      </div>
                    )}
                    <span className={`text-[9px] font-bold inline-flex items-center gap-0.5 ${blockTone} opacity-70 group-hover:opacity-100`}>
                      Notes & files <ArrowRight className="h-2.5 w-2.5" />
                    </span>
                  </Link>
                ) : (
                  <div className="mt-0.5 leading-tight" data-testid={`planner-month-block-${iso}`}>
                    <div className={`text-[9px] uppercase font-bold tracking-wider ${blockTone}`}>
                      {blockLabel}
                    </div>
                    {blockCustom && (
                      <div className={`text-[9px] font-semibold normal-case truncate ${blockTone}`}>
                        {blockCustom}
                      </div>
                    )}
                  </div>
                )
              )}

              {/* Announcement — special-day note, visible on the month */}
              {isSchoolDay && (
                annText ? (
                  <button
                    type="button"
                    onClick={() => onEditAnnouncement({ date: iso, event: announcement })}
                    data-testid={`planner-month-announcement-${iso}`}
                    className="mt-1 w-full text-left rounded px-1 py-0.5 bg-[#fef3c7] hover:bg-[#fde68a] border border-[#f5d98a] flex items-start gap-1"
                    title="Edit announcement"
                  >
                    <Megaphone className="h-2.5 w-2.5 text-[#92400e] mt-[1px] shrink-0" />
                    <span className="text-[9px] font-semibold text-[#92400e] leading-tight line-clamp-2">{annText}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onEditAnnouncement({ date: iso, event: null })}
                    data-testid={`planner-month-announcement-add-${iso}`}
                    className="mt-1 w-full text-left text-[9px] text-[#4a6984]/55 hover:text-[#92400e] inline-flex items-center gap-1"
                    title="Add an announcement"
                  >
                    <Megaphone className="h-2.5 w-2.5" /> Announcement
                  </button>
                )
              )}

              <div className="space-y-0.5 mt-1">
                {dayEvents.slice(0, 3).map((e) => (
                  <div
                    key={e.id}
                    className="text-[9px] truncate rounded px-1 py-0.5 font-semibold"
                    style={{ backgroundColor: tint(subjectColor(e.subject), 0.3), color: "#1c2d3d" }}
                  >
                    {e.activity_name}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[9px] text-[#4a6984]/70">+{dayEvents.length - 3} more</div>
                )}
              </div>

              {/* Footer — Notes (separate page) + Day plan (day view) */}
              {isSchoolDay && (
                <div className="mt-auto pt-1 flex items-center justify-between gap-1 border-t border-[#4a6984]/10">
                  <Link
                    to={`/planner/day/${iso}`}
                    data-testid={`planner-month-notes-${iso}`}
                    className="text-[9px] font-semibold text-[#4a6984] hover:text-[#1c9e7e] inline-flex items-center gap-0.5"
                    title="Open notes for this day"
                  >
                    <StickyNote className="h-2.5 w-2.5" /> Notes
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDayClick(d)}
                    data-testid={`planner-month-dayplan-${iso}`}
                    className="text-[9px] font-bold text-[#1c9e7e] hover:underline inline-flex items-center gap-0.5"
                    title="Open the day plan"
                  >
                    Day plan <ArrowRight className="h-2.5 w-2.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// Holidays / PA days / Field trips manager
// ============================================================
const HolidaysDialog = ({ open, onOpenChange, events, onCreate, onRemove }) => {
  const [date, setDate] = useState("");
  const [kind, setKind] = useState("pa_day");
  const [name, setName] = useState("");
  const blocks = (events || [])
    .filter((e) => ["holiday", "pa_day", "field_trip", "custom"].includes(e.kind) && !e.period_label)
    .sort((a, b) => a.date.localeCompare(b.date));

  const handleAdd = async () => {
    if (!date) { toast.error("Pick a date."); return; }
    const labels = { holiday: "Statutory holiday", pa_day: "PA Day" };
    const ev = await createPlannerEvent({
      date,
      kind,
      activity_name: name || labels[kind] || "Day off",
      period_label: null,
      subject: null,
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || "Could not add.");
      return null;
    });
    if (ev) {
      onCreate(ev);
      setDate(""); setName(""); setKind("pa_day");
      toast.success("Day off added.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="planner-holidays-dialog">
        <DialogHeader>
          <DialogTitle>Days off</DialogTitle>
          <DialogDescription>
            Holidays and PA days. Lessons and pushes automatically skip these dates, and the rotation cycle pauses so a PA Day on Friday slides Day-N to Monday.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[1fr_1fr] gap-2">
          <DatePickerPopover
            value={date}
            onChange={setDate}
            testid="planner-holiday-date"
            placeholder="Pick a date"
          />
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="planner-holiday-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pa_day">PA Day</SelectItem>
              <SelectItem value="holiday">Holiday</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Optional label (e.g. Family Day, Easter Monday)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="planner-holiday-name"
        />
        <Button
          onClick={handleAdd}
          className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
          data-testid="planner-holiday-add"
        >
          Add day off
        </Button>

        <div className="max-h-60 overflow-y-auto space-y-1 border-t border-[#4a6984]/10 pt-3 mt-2">
          {blocks.length === 0 && (
            <p className="text-xs text-[#4a6984]/60">No days off marked yet.</p>
          )}
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between text-xs px-2 py-1.5 bg-[#f4f5f7] rounded" data-testid={`planner-holiday-row-${b.id}`}>
              <div>
                <span className="font-bold text-[#1c2d3d]">{b.date}</span>
                <span className="text-[#4a6984] ml-2">
                  · {b.kind === "holiday" ? "Holiday" : b.kind === "pa_day" ? "PA Day" : b.kind === "field_trip" ? "Field Trip" : "Day off"}
                  {b.activity_name ? ` · ${b.activity_name}` : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemove(b)}
                aria-label="Remove"
                className="text-[#c45a6b] hover:bg-white rounded p-0.5"
                data-testid={`planner-holiday-remove-${b.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Drag subject-mismatch confirm
// ============================================================
const SubjectMismatchDialog = ({ open, draggedSubject, targetSubject, onConfirm, onCancel }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
    <DialogContent className="max-w-sm" data-testid="planner-mismatch-dialog">
      <DialogHeader>
        <DialogTitle>Move across subjects?</DialogTitle>
        <DialogDescription>
          You're dropping a <strong>{draggedSubject || "subject-less"}</strong> lesson onto a{" "}
          <strong>{targetSubject || "subject-less"}</strong> slot. Are you sure?
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="planner-mismatch-cancel">
          Cancel
        </Button>
        <Button
          className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
          onClick={onConfirm}
          data-testid="planner-mismatch-confirm"
        >
          Yes, move it
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ============================================================
// Main Planner panel
// ============================================================
// Announcement dialog — special-day note shown on the month view
// ============================================================
const AnnouncementDialog = ({ open, onOpenChange, target, onSaved, onRemoved }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setText(target?.event?.note || target?.event?.activity_name || "");
  }, [open, target]);

  if (!target) return null;

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      let event;
      if (target.event) {
        event = await updatePlannerEvent(target.event.id, { note: trimmed });
      } else {
        event = await createPlannerEvent({
          date: target.date,
          kind: "announcement",
          note: trimmed,
          activity_name: "Announcement",
          period_label: null,
        });
      }
      onSaved(event);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save announcement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="planner-announcement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-[#92400e]" /> Announcement
          </DialogTitle>
          <DialogDescription>
            {target.date} · a special note for this day (e.g. report cards home, assembly). Shows on the month view.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Report cards go home · Assembly at 1:00"
          data-testid="planner-announcement-text"
          autoFocus
        />
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {target.event && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await deletePlannerEvent(target.event.id);
                  onRemoved(target.event);
                  onOpenChange(false);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not remove.");
                }
              }}
              className="text-[#c45a6b] border-[#c45a6b]/30"
              data-testid="planner-announcement-remove"
            >
              Remove
            </Button>
          )}
          <Button
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
            onClick={handleSave}
            disabled={saving || !text.trim()}
            data-testid="planner-announcement-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============================================================
// ============================================================
// Legacy subject cleanup banner
// ============================================================
// Surfaces when a saved schedule still holds a bundled subject
// ("The Arts" / "Health and Physical Education"). Lists each legacy
// slot with an inline strand picker and a single Apply that rewrites
// the subject_map and persists it.
const LegacyCleanupBanner = ({ schedule, onApplied, onDismiss }) => {
  const legacySlots = useMemo(() => {
    const out = [];
    const map = schedule?.subject_map || {};
    Object.entries(map).forEach(([day, periods]) => {
      Object.entries(periods || {}).forEach(([period, subject]) => {
        if (LEGACY_SUBJECT_STRANDS[subject]) out.push({ day, period, subject });
      });
    });
    return out;
  }, [schedule]);

  const [choices, setChoices] = useState({});
  const [saving, setSaving] = useState(false);

  if (legacySlots.length === 0) return null;

  const slotKey = (s) => `${s.day}||${s.period}`;
  const allChosen = legacySlots.every((s) => choices[slotKey(s)]);

  const handleApply = async () => {
    setSaving(true);
    try {
      const nextMap = {};
      Object.entries(schedule.subject_map || {}).forEach(([day, periods]) => {
        nextMap[day] = { ...(periods || {}) };
      });
      for (const slot of legacySlots) {
        const chosen = choices[slotKey(slot)];
        if (chosen) nextMap[slot.day][slot.period] = chosen;
      }
      const payload = {
        schedule_type: schedule.schedule_type,
        cycle_days: schedule.cycle_days,
        periods: schedule.periods,
        subject_map: nextMap,
        color_scheme: schedule.color_scheme || {},
        cycle_anchors: schedule.cycle_anchors || [],
        week_start: schedule.week_start || "monday",
        onboarded: true,
      };
      const saved = await savePlannerSchedule(payload);
      onApplied(saved);
      toast.success("Updated your Arts / Health & PE strands.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update subjects.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-[#e59a3c]/45 bg-[#fde9d2]/45 p-4 planner-no-print"
      data-testid="planner-legacy-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-[#b45309] mt-0.5 shrink-0" />
          <div>
            <p className="font-extrabold text-[13px] text-[#1c2d3d]">
              Update your subjects to the new strands
            </p>
            <p className="text-xs text-[#4a6984] mt-0.5">
              Your schedule still uses older bundled subjects. Pick a strand for
              each slot below so your week matches the updated subject list.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-[#4a6984]/70 hover:text-[#4a6984] rounded p-0.5 shrink-0"
          data-testid="planner-legacy-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 space-y-1.5">
        {legacySlots.map((slot) => {
          const options = LEGACY_SUBJECT_STRANDS[slot.subject] || [];
          const k = slotKey(slot);
          return (
            <div
              key={k}
              className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-md bg-white/70 border border-[#4a6984]/12 px-3 py-2"
              data-testid={`planner-legacy-row-${slot.day}-${slot.period}`.replace(/\s+/g, "-")}
            >
              <div className="text-xs text-[#1c2d3d] flex-1">
                <span className="font-bold">{slot.day}</span>
                <span className="text-[#4a6984]"> · {slot.period} · </span>
                <span className="font-semibold text-[#b45309]">{slot.subject}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <ArrowRight className="h-3.5 w-3.5 text-[#4a6984]/60 hidden sm:block" />
                <Select
                  value={choices[k] || ""}
                  onValueChange={(v) => setChoices((c) => ({ ...c, [k]: v }))}
                >
                  <SelectTrigger
                    className="h-9 w-full sm:w-44 text-xs"
                    data-testid={`planner-legacy-select-${slot.day}-${slot.period}`.replace(/\s+/g, "-")}
                  >
                    <SelectValue placeholder="Pick a strand" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button
          size="sm"
          className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
          onClick={handleApply}
          disabled={saving || !allChosen}
          data-testid="planner-legacy-apply"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
          Apply
        </Button>
      </div>
    </div>
  );
};

// ============================================================
// Main Planner
// ============================================================
const Planner = () => {
  const [access, setAccess] = useState({ has_access: false, onboarded: false, loading: true });
  const [schedule, setSchedule] = useState(null);
  const [view, setView] = useState("day");          // "day" | "week" | "month"
  // Open on the day the teacher was viewing when they drilled into a period
  // and clicked "Back to planner" (?date=YYYY-MM-DD), not always today.
  const [searchParams] = useSearchParams();
  const [cursor, setCursor] = useState(() => {
    const d = searchParams.get("date");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return parseISO(d);
    return new Date();
  });
  // Honour ?date even when the planner is already mounted (e.g. returning
  // from a period's notes while the dashboard shell stays alive), and snap
  // to Day view so the teacher lands on exactly the day they left.
  const dateParam = searchParams.get("date");
  useEffect(() => {
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setCursor(parseISO(dateParam));
      setView("day");
    }
  }, [dateParam]);
  const [events, setEvents] = useState([]);
  const [dayLabelMap, setDayLabelMap] = useState({});
  const [scheduleTarget, setScheduleTarget] = useState(null);  // {date, period_label, subject}
  const [lessonTarget, setLessonTarget] = useState(null);      // PlannerEvent currently expanded
  const [prepTarget, setPrepTarget] = useState(null);          // {date, period_label, event?}
  const [editingSchedule, setEditingSchedule] = useState(false);
  // Phase 83c — drag-drop + holiday manager + print
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [mismatch, setMismatch] = useState(null);   // {eventId, target, draggedSubject}
  const [printView, setPrintView] = useState(null); // "day" | "week" | "month" | null
  // Legacy-subject cleanup banner dismissal (session-scoped).
  const [legacyDismissed, setLegacyDismissed] = useState(false);
  // Month-view announcement editor target ({date, event|null}).
  const [announcementTarget, setAnnouncementTarget] = useState(null);
  // Phase 112.C — read-only summative chips pulled from the Long Range
  // Plan. Pull-not-push: Planner only displays; the LRP card is the
  // single source of truth for the due_date.
  const [summativeChips, setSummativeChips] = useState([]);

  // Phase 113.D — "Schedule from Long Range Plan" picker + dialog.
  // Lists every unit with at least one generated-but-unscheduled lesson;
  // clicking one opens the propose → review → confirm dialog. The
  // picker re-fetches its list each time the teacher opens it so a
  // commit-elsewhere keeps the counts honest.
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [scheduleUnitTarget, setScheduleUnitTarget] = useState(null);
  const [scheduleUnits, setScheduleUnits] = useState([]);
  const [scheduleUnitsLoading, setScheduleUnitsLoading] = useState(false);
  const refreshScheduleUnits = async () => {
    setScheduleUnitsLoading(true);
    try {
      const rows = await listUnitsWithUnscheduledLessons();
      setScheduleUnits(Array.isArray(rows) ? rows : []);
    } catch {
      setScheduleUnits([]);
    } finally {
      setScheduleUnitsLoading(false);
    }
  };
  useEffect(() => {
    if (schedulePickerOpen) refreshScheduleUnits();
  }, [schedulePickerOpen]);

  // Initial access probe + schedule load
  useEffect(() => {
    plannerAccess()
      .then((res) => {
        setAccess({ ...res, loading: false });
        if (res.has_access) {
          getPlannerSchedule()
            .then((sched) => sched && setSchedule(sched))
            .catch(() => {});
        }
      })
      .catch(() => setAccess({ has_access: false, onboarded: false, loading: false }));
  }, []);

  // Compute the visible date range for the current view.
  const { rangeStart, rangeEnd, weekDates } = useMemo(() => {
    if (view === "day") {
      return { rangeStart: isoDate(cursor), rangeEnd: isoDate(cursor), weekDates: [cursor] };
    }
    if (view === "week") {
      const dow = cursor.getDay() === 0 ? 6 : cursor.getDay() - 1; // Mon=0
      const start = addDays(cursor, -dow);
      const dates = Array.from({ length: 5 }, (_, i) => addDays(start, i));
      return { rangeStart: isoDate(dates[0]), rangeEnd: isoDate(dates[4]), weekDates: dates };
    }
    // month
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() === 0 ? 6 : first.getDay() - 1);
    const gridStart = addDays(first, -offset);
    const gridEnd = addDays(gridStart, 41);
    return { rangeStart: isoDate(gridStart), rangeEnd: isoDate(gridEnd), weekDates: [] };
  }, [view, cursor]);

  // Load events for the visible range whenever it changes.
  useEffect(() => {
    if (!access.has_access || !schedule?.onboarded) return;
    listPlannerEvents(rangeStart, rangeEnd)
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [access.has_access, schedule, rangeStart, rangeEnd]);

  // Load Long Range Plan summative chips for the visible range (Pro
  // users only — the endpoint is gated and 403s for non-Pro accounts,
  // which we silently swallow so the Planner still renders).
  useEffect(() => {
    if (!access.has_access || !schedule?.onboarded) return;
    getLrpSummativeChips(rangeStart, rangeEnd)
      .then((res) => setSummativeChips(res?.chips || []))
      .catch(() => setSummativeChips([]));
  }, [access.has_access, schedule, rangeStart, rangeEnd]);

  // Compute day labels (rotation cycles) for the visible range.
  useEffect(() => {
    if (!access.has_access || !schedule?.onboarded || schedule.schedule_type !== "rotation") {
      setDayLabelMap({});
      return;
    }
    const dates = [];
    let cur = parseISO(rangeStart);
    const end = parseISO(rangeEnd);
    while (cur <= end) {
      dates.push(isoDate(cur));
      cur = addDays(cur, 1);
    }
    plannerDayLabels(dates)
      .then((res) => setDayLabelMap(res.labels || {}))
      .catch(() => setDayLabelMap({}));
  }, [access.has_access, schedule, rangeStart, rangeEnd]);

  // Phase 83c — derive whole-day blocks by date for view rendering.
  // Must be above the gates so the hook order stays stable.
  const blocksByDate = useMemo(() => {
    const out = {};
    for (const e of events) {
      if (["holiday", "pa_day", "field_trip", "custom"].includes(e.kind) && !e.period_label) {
        out[e.date] = e;
      }
    }
    return out;
  }, [events]);

  // Whole-day announcements keyed by date (special-day notes shown on
  // the month view).
  const announcementsByDate = useMemo(() => {
    const out = {};
    for (const e of events) {
      if (e.kind === "announcement" && !e.period_label) out[e.date] = e;
    }
    return out;
  }, [events]);

  // ----- Gates -----
  if (access.loading) {
    return <p className="text-[#4a6984]/70" data-testid="planner-loading">Loading planner…</p>;
  }
  if (!access.has_access) {
    return <ProUpsell />;
  }
  if (editingSchedule || !schedule?.onboarded) {
    return (
      <SetupWizard
        initial={schedule}
        onSaved={(saved) => {
          setSchedule({ ...(schedule || {}), ...saved });
          setEditingSchedule(false);
        }}
      />
    );
  }

  // Day label for the current cursor in single-day view.
  const cursorISO = isoDate(cursor);
  const cursorDayLabel =
    schedule.schedule_type === "weekly"
      ? (cursor.getDay() === 0 || cursor.getDay() === 6 ? null : WEEKDAYS[(cursor.getDay() + 6) % 7])
      : dayLabelMap[cursorISO] || null;

  // Week labels
  const weekDayLabels = weekDates.map((d) =>
    schedule.schedule_type === "weekly"
      ? (d.getDay() === 0 || d.getDay() === 6 ? null : WEEKDAYS[(d.getDay() + 6) % 7])
      : dayLabelMap[isoDate(d)] || null
  );

  const navigate = (delta) => {
    if (view === "day") setCursor(addDays(cursor, delta));
    else if (view === "week") setCursor(addDays(cursor, delta * 7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  };

  const handleRemove = async (ev) => {
    try {
      await deletePlannerEvent(ev.id);
      setEvents((arr) => arr.filter((x) => x.id !== ev.id));
      toast.success("Removed from planner.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove.");
    }
  };

  const reloadRange = () => {
    listPlannerEvents(rangeStart, rangeEnd).then(setEvents).catch(() => {});
  };

  // Area 2 write-in save (period_note event holds the one-line note + the
  // workspace notes/links/attachments).
  const handleSaveWriteIn = async (date, periodLabel, text, metaEvent) => {
    let ev;
    if (metaEvent) {
      ev = await updatePlannerEvent(metaEvent.id, { note: text });
    } else {
      ev = await createPlannerEvent({
        date,
        period_label: periodLabel,
        kind: "period_note",
        note: text,
        activity_name: "Period",
      });
    }
    setEvents((arr) => [...arr.filter((x) => x.id !== ev.id), ev]);
    return ev;
  };

  const handleOpenLesson = (ev) => {
    if (!ev) return;
    setLessonTarget(ev);
  };

  // Phase 83c — drop-event handler with subject-mismatch confirmation.
  const performMove = async ({ eventId, target }) => {
    try {
      const updated = await updatePlannerEvent(eventId, {
        date: target.date,
        period_label: target.period_label,
      });
      setEvents((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
      toast.success("Moved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move.");
    }
  };

  const handleDropEvent = ({ eventId, target, draggedSubject }) => {
    // Same-cell drop is a no-op.
    const ev = events.find((e) => e.id === eventId);
    if (!ev || (ev.date === target.date && ev.period_label === target.period_label)) return;
    const dragged = (draggedSubject || ev.subject || "").trim().toLowerCase();
    const tgt = (target.subject || "").trim().toLowerCase();
    if (dragged && tgt && dragged !== tgt) {
      setMismatch({ eventId, target, draggedSubject: draggedSubject || ev.subject });
      return;
    }
    performMove({ eventId, target });
  };

  // Phase 83c — single-button print that scopes a CSS class to body
  // for the duration of `window.print()`.
  const handlePrint = () => {
    const tag = `planner-print-${view}`;
    document.body.classList.add("planner-printing", tag);
    setPrintView(view);
    // Give React a microtask to render the print-only header before the OS
    // print dialog snapshots the page.
    setTimeout(() => {
      window.print();
      document.body.classList.remove("planner-printing", tag);
      setPrintView(null);
    }, 100);
  };

  return (
    <div className="space-y-4" data-testid="planner-root">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="inline-flex rounded-lg bg-white border border-[#4a6984]/15 p-1">
          {[
            { v: "day", l: "Day", icon: CalendarIcon },
            { v: "week", l: "Week", icon: CalendarRange },
            { v: "month", l: "Month", icon: CalendarDays },
          ].map((b) => (
            <button
              key={b.v}
              type="button"
              onClick={() => setView(b.v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${view === b.v ? "bg-[#1c2d3d] text-white" : "text-[#4a6984]"}`}
              data-testid={`planner-view-${b.v}`}
            >
              <b.icon className="h-3.5 w-3.5 inline mr-1" />{b.l}
            </button>
          ))}
        </div>

        <div className="inline-flex flex-wrap items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="planner-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="planner-today">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(1)} data-testid="planner-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSchedulePickerOpen(true)}
            data-testid="planner-schedule-from-lrp"
            className="text-[#4a6984]"
          >
            <CalendarClock className="h-4 w-4 mr-1" /> Schedule from LRP
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHolidaysOpen(true)}
            data-testid="planner-holidays"
            className="text-[#4a6984]"
          >
            <Ban className="h-4 w-4 mr-1" /> Days off
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrint}
            data-testid="planner-print"
            className="text-[#4a6984]"
          >
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditingSchedule(true)}
            data-testid="planner-settings"
            className="text-[#4a6984]"
          >
            <SettingsIcon className="h-4 w-4 mr-1" /> Schedule
          </Button>
        </div>
      </div>

      {/* Print-only header (visible only when window.print is firing) */}
      <div className="planner-print-header hidden">
        <h2 className="font-extrabold text-2xl text-[#1c2d3d]">My AtomicEdu Planner</h2>
        <p className="text-sm text-[#4a6984]">
          {view === "day" && fmtLongDate(cursor)}
          {view === "week" && weekDates.length > 0 && `Week of ${fmtShortDate(weekDates[0])} – ${fmtShortDate(weekDates[4])}, ${weekDates[0].getFullYear()}`}
          {view === "month" && cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </p>
      </div>

      {/* Range label */}
      <div className="text-sm text-[#4a6984] font-semibold">
        {view === "day" && fmtLongDate(cursor)}
        {view === "week" && weekDates.length > 0 && `${fmtShortDate(weekDates[0])} – ${fmtShortDate(weekDates[4])}, ${weekDates[0].getFullYear()}`}
        {view === "month" && cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
      </div>

      {/* Legacy subject cleanup */}
      {!legacyDismissed && (
        <LegacyCleanupBanner
          schedule={schedule}
          onApplied={(saved) => setSchedule(saved)}
          onDismiss={() => setLegacyDismissed(true)}
        />
      )}

      {/* Views */}
      {view === "day" && (
        <DayPlanView
          schedule={schedule}
          events={events}
          dayLabel={cursorDayLabel}
          currentDate={cursor}
          onSchedule={setScheduleTarget}
          onOpenLesson={handleOpenLesson}
          onSaveWriteIn={handleSaveWriteIn}
          onDropEvent={handleDropEvent}
          dayBlock={blocksByDate[isoDate(cursor)] || null}
        />
      )}
      {view === "week" && (
        <WeekView
          schedule={schedule}
          events={events}
          dates={weekDates}
          dayLabels={weekDayLabels}
          onSchedule={setScheduleTarget}
          onOpenLesson={handleOpenLesson}
          onSaveWriteIn={handleSaveWriteIn}
          onDropEvent={handleDropEvent}
          blocksByDate={blocksByDate}
          summativeChips={summativeChips}
        />
      )}
      {view === "month" && (
        <MonthView
          schedule={schedule}
          events={events}
          monthDate={cursor}
          dayLabelMap={dayLabelMap}
          blocksByDate={blocksByDate}
          announcementsByDate={announcementsByDate}
          onEditAnnouncement={setAnnouncementTarget}
          onDayClick={(d) => { setCursor(d); setView("day"); }}
        />
      )}

      {/* Scheduling popover */}
      {scheduleTarget && (
        <ScheduleLessonDialog
          open={!!scheduleTarget}
          onOpenChange={(o) => !o && setScheduleTarget(null)}
          date={scheduleTarget.date}
          periodLabel={scheduleTarget.period_label}
          subject={scheduleTarget.subject}
          onScheduled={(ev) => setEvents((arr) => [...arr.filter(x => x.id !== ev.id), ev])}
        />
      )}

      {/* Lesson resource dialog (click a scheduled lesson) */}
      <LessonResourceDialog
        open={!!lessonTarget}
        onOpenChange={(o) => !o && setLessonTarget(null)}
        event={lessonTarget}
        onUpdate={(ev) => setEvents((arr) => arr.map((x) => (x.id === ev.id ? ev : x)))}
        onRemove={async (ev) => { await handleRemove(ev); setLessonTarget(null); }}
        onPushed={() => { reloadRange(); }}
      />

      {/* Prep period notes dialog */}
      <PrepNoteDialog
        open={!!prepTarget}
        onOpenChange={(o) => !o && setPrepTarget(null)}
        target={prepTarget}
        onSaved={(ev) => setEvents((arr) => [...arr.filter(x => x.id !== ev.id), ev])}
        onRemove={(ev) => handleRemove(ev)}
      />

      {/* Month-view announcement editor */}
      <AnnouncementDialog
        open={!!announcementTarget}
        onOpenChange={(o) => !o && setAnnouncementTarget(null)}
        target={announcementTarget}
        onSaved={(ev) => setEvents((arr) => [...arr.filter(x => x.id !== ev.id), ev])}
        onRemoved={(ev) => setEvents((arr) => arr.filter(x => x.id !== ev.id))}
      />

      {/* Holidays / PA days / Field trips */}
      <HolidaysDialog
        open={holidaysOpen}
        onOpenChange={setHolidaysOpen}
        events={events}
        onCreate={(ev) => setEvents((arr) => [...arr, ev])}
        onRemove={async (ev) => { await handleRemove(ev); }}
      />

      {/* Subject-mismatch confirm (drag-drop) */}
      <SubjectMismatchDialog
        open={!!mismatch}
        draggedSubject={mismatch?.draggedSubject}
        targetSubject={mismatch?.target?.subject}
        onConfirm={() => { performMove(mismatch); setMismatch(null); }}
        onCancel={() => setMismatch(null)}
      />

      {/* Phase 113.D — "Schedule from Long Range Plan" picker. Lists
          units with at least one unscheduled lesson. Clicking one
          opens the propose-review-confirm dialog below. */}
      <Dialog open={schedulePickerOpen} onOpenChange={setSchedulePickerOpen}>
        <DialogContent
          className="max-w-lg bg-white"
          data-testid="planner-schedule-from-lrp-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[#1c2d3d] font-extrabold flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-[#1c9e7e]" />
              Schedule from Long Range Plan
            </DialogTitle>
            <DialogDescription className="text-[#4a6984]">
              Pick a unit to auto-place its remaining lessons. You&apos;ll review
              the placements before anything is scheduled.
            </DialogDescription>
          </DialogHeader>
          {scheduleUnitsLoading ? (
            <div className="flex items-center gap-2 py-6 text-[#4a6984]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your units…
            </div>
          ) : scheduleUnits.length === 0 ? (
            <div className="py-6 text-center text-[#4a6984] text-sm">
              <p>No units have unscheduled lessons right now.</p>
              <p className="mt-1 text-xs text-[#4a6984]/65">
                Generate lessons inside a unit, then they&apos;ll show up here ready to schedule.
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
              {scheduleUnits.map((u) => (
                <li key={u.unit_activity_id}>
                  <button
                    type="button"
                    data-testid={`planner-schedule-pick-${u.unit_activity_id}`}
                    onClick={() => {
                      setScheduleUnitTarget(u);
                      setSchedulePickerOpen(false);
                    }}
                    className="w-full text-left rounded-md border border-[#96ded1] bg-[#96ded1]/12 hover:bg-[#96ded1]/25 transition-colors px-3 py-2"
                  >
                    <p className="font-extrabold text-[13px] text-[#1c2d3d] truncate">
                      {u.unit_name}
                    </p>
                    <p className="text-[11px] text-[#4a6984] truncate">
                      {u.subject || "—"} · {u.unscheduled_count} unscheduled of {u.total_lessons}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSchedulePickerOpen(false)}
              data-testid="planner-schedule-from-lrp-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {scheduleUnitTarget ? (
        <ScheduleRemainingLessonsDialog
          open={Boolean(scheduleUnitTarget)}
          onOpenChange={(o) => !o && setScheduleUnitTarget(null)}
          unitActivityId={scheduleUnitTarget.unit_activity_id}
          unitName={scheduleUnitTarget.unit_name}
          onCommitted={() => {
            setScheduleUnitTarget(null);
            reloadRange();
            refreshScheduleUnits();
          }}
        />
      ) : null}

      {/* Tiny help footer */}
      <p className="text-xs text-[#4a6984]/60 mt-3 flex items-center gap-1.5 planner-no-print">
        <Sparkles className="h-3 w-3" />
        Click a lesson to grab resources. Drag to move it. Click an empty slot to plan it.
      </p>
    </div>
  );
};

export default Planner;
